import React, { ReactNode, useMemo } from "react";
import useThemeValue from "src/Modules/ThemeModule/Hooks/useThemeValue";
import { FONT_TYPES, VARIANT } from "src/Modules/ThemeModule/Types/CommonTypes";
import classNames from "classnames";

export type TypographyProps = Omit<React.HTMLProps<HTMLElement>, "size"> & {
  children?: ReactNode;
  variant?: VARIANT;
  type?: FONT_TYPES;
  size?: "xxs" | "xs" | "s" | "m" | "l" | "xl" | "xxl" | "xxxl" | "xxxxl";
  marginVertical?: number | string;
  marginHorizontal?: number | string;
  paddingVertical?: number | string;
  paddingHorizontal?: number | string;
  component?: React.ElementType; // Allow custom component type (e.g., h1, h2, p, etc.)
} & React.CSSProperties; // Extend to include all standard text props like style, onClick, etc.

const sizesObj = {
  xxs: "text-[8px]",
  xs: "text-xs",
  s: "text-sm",
  m: "text-base",
  l: "text-lg",
  xl: "text-xl",
  xxl: "text-2xl",
  xxxl: "text-3xl",
  xxxxl: "text-4xl",
};

const Typography = ({
  children,
  variant = "onSurface",
  type = "regular",
  size = "m",
  component: Component = "span", // Default to 'p' tag if no component is provided
  className,
  pointerEvents,
  marginVertical,
  marginHorizontal,
  paddingVertical,
  paddingHorizontal,
  ...restProps // Capture the rest of the HTML props
}: TypographyProps) => {
  const theme = useThemeValue();
  const variantColor = variant ? theme.colors[variant] : theme.colors.text;
  const { fonts } = useThemeValue();
  const { fontWeight, fontFamily } = fonts[type];

  // Generate Tailwind classes based on props
  const tailwindClasses = useMemo(() => {
    return classNames(
      // Base classes
      className,
      // Margin
      marginVertical !== undefined && `my-${marginVertical}`,
      marginHorizontal !== undefined && `mx-${marginHorizontal}`,
      // Padding
      paddingVertical !== undefined && `py-${paddingVertical}`,
      paddingHorizontal !== undefined && `px-${paddingHorizontal}`,
      // Pointer events
      pointerEvents === "none" && "pointer-events-none",
      pointerEvents === "auto" && "pointer-events-auto",
      // Ensure Block behaves as a flex item when inside a flex container
      `font-${type}`,
      sizesObj[size],
      `text-${variant}`
    );
  }, [
    className,
    marginVertical,
    marginHorizontal,
    paddingVertical,
    paddingHorizontal,
    pointerEvents,
    type,
    size,
    variant,
  ]);

  const customStyles = useMemo(() => {
    const styles: React.CSSProperties = {};

    if (marginVertical !== undefined) {
      styles.marginTop = marginVertical;
      styles.marginBottom = marginVertical;
    }

    if (marginHorizontal !== undefined) {
      styles.marginLeft = marginHorizontal;
      styles.marginRight = marginHorizontal;
    }

    if (paddingVertical !== undefined) {
      styles.paddingTop = paddingVertical;
      styles.paddingBottom = paddingVertical;
    }

    if (paddingHorizontal !== undefined) {
      styles.paddingLeft = paddingHorizontal;
      styles.paddingRight = paddingHorizontal;
    }

    return styles;
  }, [marginVertical, marginHorizontal, paddingVertical, paddingHorizontal]);

  return (
    <Component
      className={tailwindClasses}
      style={{
        // color: variantColor ? variantColor : undefined,
        // fontFamily,
        // fontWeight,
        ...customStyles,
        ...restProps,
      }}
      {...restProps} // Spread all other props (e.g., onClick, id, etc.)
    >
      {children}
    </Component>
  );
};

export default Typography;
